import type { MarketId } from '@morpho-org/blue-sdk'
import type { ApiInputToken, MarketRewards } from '@morpho-org/dapp-core'

import type { aggregateMarketWarnings } from '@/helpers/aggregateMarketWarnings'
import type { RefinanceMarket } from '@/lib/procedures/marketRefinanceMarkets.contract'
import type { MarketWarning } from '@/lib/procedures/marketWarnings'

import { MarketWarningType } from '@/helpers/warnings'

type RefinanceMarketAsset = RefinanceMarket['loanAsset']
type MarketWarningFields = Parameters<
  typeof aggregateMarketWarnings
>[0]['market']['warnings'][number]

function toAsset(asset: RefinanceMarketAsset, chainId: number): ApiInputToken {
  return {
    address: asset.address,
    symbol: asset.symbol,
    name: asset.name ?? undefined,
    decimals: asset.decimals,
    logoURI: asset.logoUri,
    priceUsd: asset.priceUsd,
    chain: { id: chainId }
  }
}

/** The warning in the shape the market warning helpers read; only two types carry metadata. */
export function toMarketWarningFields(warning: MarketWarning): MarketWarningFields {
  const metadata = (() => {
    switch (warning.type) {
      case MarketWarningType.BAD_DEBT_UNREALIZED:
        return {
          __typename: 'BadDebtUnrealizedMarketWarningMetadata' as const,
          badDebtUsd: warning.badDebtUsd,
          badDebtAssets: warning.badDebtAssets ?? '0'
        }
      case MarketWarningType.CUSTOM:
        return { __typename: 'CustomMetadata' as const, content: warning.content }
      default:
        return null
    }
  })()
  return {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- the bridge sends `red` or `yellow`
    level: warning.level.toUpperCase() as MarketWarningFields['level'],
    type: warning.type,
    metadata
  }
}

/** Borrow rewards in the shape `getMarketRewards` reads; a reward row renders symbol and logo only. */
function toBorrowRewards(market: RefinanceMarket): MarketRewards[] {
  return market.rewards.borrow.map(reward => ({
    borrowApr: reward.apr,
    asset: {
      address: reward.token.address,
      symbol: reward.token.symbol ?? undefined,
      decimals: 18,
      logoURI: reward.token.logoUri,
      chain: { id: reward.token.chainId }
    }
  }))
}

/**
 * A refinance market in the shapes the dialog's consumers read: the market selector's rows, the
 * recap's rewards, `parseApiAsset` and `converter.getMarketParams`.
 */
export function toRefinanceDialogMarket(market: RefinanceMarket) {
  const { chainId, collateralAsset } = market
  return {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- hexIdSchema is a market id
    marketId: market.marketId as MarketId,
    loanAsset: toAsset(market.loanAsset, chainId),
    collateralAsset: collateralAsset && {
      ...toAsset(collateralAsset, chainId),
      yield: collateralAsset.yield && { apy: collateralAsset.yield.apy },
      // A Vault V1 share: `parseApiAsset` builds a `VaultToken` whose yield is the vault's APY.
      vault: collateralAsset.vault && {
        name: collateralAsset.vault.name,
        address: collateralAsset.vault.address,
        asset: { logoURI: collateralAsset.vault.assetLogoUri },
        state: { avgNetApy: collateralAsset.vault.avgNetApy }
      }
    },
    oracle: { address: market.oracleAddress },
    irmAddress: market.irmAddress,
    lltv: market.lltv,
    reallocatableLiquidityAssets: market.reallocatableLiquidityAssets,
    warnings: market.warnings.map(toMarketWarningFields),
    state: {
      avgBorrowApy: market.avgBorrowApy,
      avgNetBorrowApy: market.avgNetBorrowApy,
      liquidityAssets: market.liquidityAssets,
      liquidityAssetsUsd: market.liquidityAssetsUsd,
      rewards: toBorrowRewards(market)
    }
  }
}

export type RefinanceDialogMarket = ReturnType<typeof toRefinanceDialogMarket>
